import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CheckIcon, Loader2Icon } from 'lucide-react';

import { useLanguage } from '@/contexts/language-context';
import { Button } from '@/components/ui/button';
import { ViewToggle } from '@/components/ui/view-toggle';
import { createCheckoutSession, type BillingCycle } from '@/lib/api/billing';
import { listTiers, TIERS_QUERY_KEY, type TierDto } from '@/lib/api/tiers';

import { formatPrice } from './helpers';

interface PlanCardProps {
  tier: TierDto;
  cycle: BillingCycle;
  current: boolean;
  busy: boolean;
  onSelect: () => void;
}

function PlanCard({ tier, cycle, current, busy, onSelect }: PlanCardProps) {
  const { t } = useLanguage();
  const cents = cycle === 'monthly' ? tier.monthly_price_cents : tier.annual_price_cents;
  const suffix = cycle === 'monthly' ? t.billingPerMonth : t.billingPerYear;

  return (
    <div
      className="relative flex flex-col gap-3 rounded-xl border bg-card p-5"
      data-testid="billing-plan-card"
    >
      <div className="flex items-baseline justify-between">
        <span className="text-lg font-bold">{tier.label}</span>
        <span className="text-2xl font-bold">
          {formatPrice(cents)}
          <span className="text-sm font-normal text-muted-foreground">{suffix}</span>
        </span>
      </div>

      <Button
        type="button"
        size="sm"
        variant={current ? 'outline' : 'default'}
        disabled={busy || current}
        onClick={onSelect}
        data-testid="billing-plan-select"
      >
        {busy && <Loader2Icon className="w-3.5 h-3.5 animate-spin" />}
        {current ? (
          <>
            <CheckIcon className="w-3.5 h-3.5" />
            {t.billingCurrentPlan}
          </>
        ) : (
          t.billingChangePlan
        )}
      </Button>
    </div>
  );
}

interface PlanPickerProps {
  businessId: string;
  currentTierId: string | null;
}

/**
 * Monthly/annual toggle + tier cards. Selecting a plan starts Stripe
 * Checkout and redirects the browser — mirrors the mint/redirect mutation
 * shape in src/routes/business-page/calendar-sync-section.tsx.
 */
export function PlanPicker({ businessId, currentTierId }: PlanPickerProps) {
  const { t } = useLanguage();
  const [cycle, setCycle] = useState<BillingCycle>('monthly');
  const [pendingTierId, setPendingTierId] = useState<string | null>(null);

  const tiersQuery = useQuery({ queryKey: TIERS_QUERY_KEY, queryFn: listTiers });
  const activeTiers = (tiersQuery.data ?? []).filter((tier) => tier.is_active);

  const checkoutMutation = useMutation({
    mutationFn: (tierId: string) =>
      createCheckoutSession(businessId, { tier_id: tierId, billing_cycle: cycle }),
    meta: { errorMessage: t.billingCheckoutError },
    onSuccess: (data) => {
      window.location.href = data.checkout_url;
    },
    onSettled: () => setPendingTierId(null),
  });

  function handleSelect(tierId: string) {
    setPendingTierId(tierId);
    checkoutMutation.mutate(tierId);
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold">{t.billingSubscribe}</h2>
        <ViewToggle
          value={cycle}
          onChange={setCycle}
          aria-label={t.billingCycleMonthly}
          options={[
            { value: 'monthly', label: t.billingCycleMonthly },
            { value: 'annual', label: t.billingCycleAnnual },
          ]}
        />
      </div>

      {tiersQuery.isPending && (
        <div className="flex items-center justify-center py-10 text-muted-foreground">
          <Loader2Icon className="w-5 h-5 animate-spin" />
        </div>
      )}

      {!tiersQuery.isPending && (
        <div className="grid gap-3 sm:grid-cols-3">
          {activeTiers.map((tier) => (
            <PlanCard
              key={tier.id}
              tier={tier}
              cycle={cycle}
              current={tier.id === currentTierId}
              busy={checkoutMutation.isPending && pendingTierId === tier.id}
              onSelect={() => handleSelect(tier.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
